import * as Sentry from '@sentry/browser';

import {
  LOAD_CART_FAILURE,
  LOAD_CART_REQUEST,
  LOAD_CART_SUCCESS,
  UPDATE_CART_FAILURE,
  UPDATE_CART_MONOGRAM,
  UPDATE_CART_REQUEST,
  UPDATE_CART_SUCCESS,
} from 'dux/checkoutCart/actionTypes';
import { getCartData, getShouldProposePumps, getWantsPumps } from 'dux/checkoutCart/selectors';
import * as userSelectors from 'dux/user/selectors';

import * as CartService from 'Services/CartService';

import { defaultFrequency } from 'constants/subscriptions.js';
import addOriginParam from 'utils/addOriginParam';
import optimisticCartUpdate from 'utils/optimisticCartUpdate';
import { fetchSomething } from 'utils/reduxUtils';

/**
 * PATCH
 */

export const savePumps = ({ values }) => async (dispatch, getState) => {
  const state = getState();
  const shouldProposePumps = getShouldProposePumps(state);
  const wantsPumps = getWantsPumps(state);

  // Save pump preference if needed
  if (shouldProposePumps && wantsPumps !== values.wantsPumps) {
    const data = await CartService.patch({
      wants_pumps: values.wantsPumps,
    });
    dispatch({ type: UPDATE_CART_SUCCESS, data });
  }
};

// Update cart from a list of items that changed
export const updateCart = items => async (dispatch, getState) => {
  const state = getState();
  const previousData = getCartData(state);
  const optimisticData = optimisticCartUpdate(previousData, items);
  // Hack: report the items in the action so the trackers can use it
  dispatch({ type: UPDATE_CART_REQUEST, data: optimisticData, items });

  try {
    const data = await CartService.queuePatchItems(items);
    dispatch({ type: UPDATE_CART_SUCCESS, data });
  } catch (error) {
    // reverts data because previous action didn't work
    dispatch({ type: UPDATE_CART_FAILURE, data: previousData, error });
  }
};

export const updateItemQuantity = (item, quantity) => async (dispatch, getState) =>
  updateCart([
    {
      category: item.category,
      product: item.product,
      quantity,
      type: item.type,
      customization: item.customization,
      subscription: item.subscription,
    },
  ])(dispatch, getState);

export const addToCartAndDefaultToSubscribeWhenPossible = item => async (dispatch, getState) =>
  updateCart([
    {
      category: item.category,
      product: item.product,
      quantity: 1, // Hardcoded when clicking on `ADD` button
      type: item.type,
      customization: item.customization,
      subscription: item?.subscription?.subscribable
        ? { active: true, frequency: item?.subscription?.recommended_frequency ?? defaultFrequency } // hardcoded default frequency
        : null,
    },
  ])(dispatch, getState);

export const updateItemSubscription = (item, { active, frequency }) => async (dispatch, getState) =>
  updateCart([
    {
      category: item.category,
      product: item.product,
      quantity: item.quantity,
      type: item.type,
      customization: item.customization,
      subscription: { active, frequency },
    },
  ])(dispatch, getState);

export const updateMonogram = (monogram, item) => async dispatch => {
  dispatch({ type: UPDATE_CART_MONOGRAM, item, monogram });

  try {
    await CartService.patch({
      items: [
        {
          category: item.category,
          product: item.product,
          type: item.type,
          quantity: item.quantity,
          customization: { monograms: monogram },
        },
      ],
    });
  } catch (error) {
    dispatch({ type: UPDATE_CART_MONOGRAM, item, monogram: item.customization.monograms });
    Sentry.captureMessage('Failed updating monogram on cart');
    throw error;
  }
};

export const goToCheckout = ({ history, location }) => async (dispatch, getState) => {
  const state = getState();
  const previousCart = getCartData(state);
  const user = userSelectors.getUser(state);
  const params = new URLSearchParams(location.search);
  const withOrigin = addOriginParam(params);
  dispatch({ type: UPDATE_CART_REQUEST, data: previousCart });

  try {
    const updatedCart = await CartService.patch({ is_in_checkout: true });
    dispatch({ type: UPDATE_CART_SUCCESS, data: updatedCart });

    if (!user.phone || !updatedCart.shipping_address) {
      // New users arrive here
      history(withOrigin('/checkout/contact-address'));
    } else if (updatedCart.total_price_with_taxes) {
      // Returning users without free order
      history(withOrigin('/checkout/payment'));
    } else {
      // Returning users with free order
      history(withOrigin('/checkout/confirmation'));
    }
  } catch (error) {
    // Reverts data because previous action didn't work
    dispatch({ type: UPDATE_CART_FAILURE, data: previousCart, error });
  }
};

/**
 * POST
 */

export const loadCart = fetchSomething(
  [LOAD_CART_REQUEST, LOAD_CART_SUCCESS, LOAD_CART_FAILURE],
  params => CartService.post(params)
);
